import posthog from 'posthog-js';

import type { AnalyticsAdapter, AnalyticsConfig, AnalyticsEvent, GroupIdentity, UserIdentity } from './types';

const REACTV6_PAGETRACKING_MODE = '2025-05-24';

function requiresInit(
  _target: object,
  _propertyKey: string | symbol,
  descriptor: PropertyDescriptor
): PropertyDescriptor {
  const originalMethod = descriptor.value as (...args: unknown[]) => unknown;

  descriptor.value = function (this: PostHogAdapter, ...args: unknown[]): unknown {
    if (!this.initialised) return;
    return originalMethod.apply(this, args);
  };

  return descriptor;
}

class PostHogAdapter implements AnalyticsAdapter {
  private readonly config: AnalyticsConfig;
  protected initialised = false;

  constructor(config: AnalyticsConfig) {
    this.config = config;
  }

  @requiresInit
  identify(identity: UserIdentity): void {
    posthog.identify(identity.distinctId, identity.properties, identity.propertiesToSetOnce);
  }

  init(): void {
    if (this.initialised) return;

    posthog.init(this.config.apiKey, {
      api_host: this.config.host,
      defaults: REACTV6_PAGETRACKING_MODE,
      persistence: 'memory',
    });

    this.initialised = true;
  }

  @requiresInit
  setGroup(group: GroupIdentity): void {
    posthog.group(group.groupType, group.groupKey, group.properties);
  }

  @requiresInit
  trackEvent(event: AnalyticsEvent): void {
    posthog.capture(event.event, event.properties, event.captureOptions);
  }

  @requiresInit
  trackPageView(path?: string): void {
    posthog.capture('$pageview', path ? { $current_url: path } : undefined);
  }

  @requiresInit
  reset(): void {
    posthog.reset();
  }
}

export { PostHogAdapter };
